<template>
  <q-inner-loading v-if="loading" showing color="secondary" />
  <property-report-settings
    v-else-if="report"
    :report="report"
    :initial-step="initialStep"
    @submit="submit"
    @delete="deleteSubscription"
    @cancel="safelyNavigate"
  />
</template>

<script lang="ts">
import type { AccessUser, PropertyReportSubscriptionAttributes } from 'common'
import { usePropertyReportStore } from '@/stores/propertyreport'
import PropertyReportSettings from '@/components/PropertyReports/PropertyReportSettings.vue'
import { useUserStore } from '@/stores/user'
import type { RouteLocation } from 'vue-router'
import { isLoaded } from '@/utils/loading'
import type { PropType } from 'vue'

export default {
  name: 'PropertyReportEditView',
  components: {
    PropertyReportSettings
  },
  beforeRouteLeave(to, from, next) {
    this.handleRouteChange(next)
  },
  beforeRouteUpdate(to, from, next) {
    this.handleRouteChange(next)
  },
  props: {
    reportId: {
      required: true,
      type: String
    },
    initialStep: {
      required: false,
      type: String as PropType<'comp-selection' | 'report-settings'>,
      default: 'comp-selection'
    }
  },
  data() {
    return {
      loading: false,
      report: null as PropertyReportSubscriptionAttributes | null,
      hasUnsavedChanges: true
    }
  },
  computed: {
    organizationMembers() {
      return useUserStore().organizationMembers
    }
  },
  watch: {
    organizationMembers() {
      this.checkAuthorization()
    }
  },
  async mounted() {
    this.loading = true
    await usePropertyReportStore()
      .getReport(parseInt(this.reportId))
      .then((response) => {
        if (isLoaded(response)) {
          this.report = response.data
          this.checkAuthorization()
        } else {
          this.$router.push({ name: 'Dashboard' })
        }
      })
      .finally(() => {
        this.loading = false
      })
  },
  methods: {
    handleRouteChange(next: (value?: boolean) => void) {
      if (this.hasUnsavedChanges) {
        void this.showUnsavedChangesDialog().then((shouldLeave) => {
          if (shouldLeave) {
            next()
          } else {
            next(false)
          }
        })
      } else {
        next()
      }
    },
    checkAuthorization() {
      const ownerIds = this.organizationMembers.map((_) => _.id)
      const ownerId = this.report?.user_id
      if (ownerIds.length && ownerId && !ownerIds.includes(ownerId)) {
        this.$router.push({ name: 'Dashboard' })
      }
    },
    async submit(
      report: PropertyReportSubscriptionAttributes,
      invitePeopleList: AccessUser[],
      removePeopleList: AccessUser[],
      oldOwnerId: string | null
    ) {
      if (!report) {
        console.warn('No report to update')
        return
      }
      const hasNewOwner = report.user_id !== oldOwnerId
      await usePropertyReportStore()
        .update(report)
        .then(async (editedReport) => {
          this.hasUnsavedChanges = false
          if (editedReport) {
            if (hasNewOwner) {
              const oldOwner = this.organizationMembers.find((_) => _.id === oldOwnerId)
              const newOwner = this.organizationMembers.find((_) => _.id === report.user_id)
              if (oldOwner && newOwner) {
                // add the old owner to the list of people with access
                await usePropertyReportStore().createAccess(editedReport.id, false, [
                  { user_id: oldOwner.id, email: oldOwner.email }
                ])
                // remove the new owner from the list of people with access
                removePeopleList.push(newOwner)
              }
            }
            // Invite people if there are any in the list
            if (invitePeopleList.length > 0) {
              await usePropertyReportStore().createAccess(
                editedReport.id,
                true,
                invitePeopleList.map((_) => ({ user_id: _.id, email: _.email }))
              )
            }

            // Remove people if there are any in the list
            await Promise.all(
              removePeopleList.map((user) =>
                usePropertyReportStore().deleteAccess(editedReport.id, user.id || user.email)
              )
            )
            this.$tracker.updatedReport(editedReport.id, 'property')
            this.$router.push({ name: 'Property Report', params: { reportId: editedReport.id } })
          } else {
            this.$q.notify({
              message: 'Failed to update report',
              color: 'negative',
              position: 'top'
            })
            console.error('Failed to update report', res)
          }
        })
    },
    async deleteSubscription() {
      this.hasUnsavedChanges = false
      await usePropertyReportStore()
        .delete(parseInt(this.reportId))
        .then(() => {
          this.$tracker.deletedReport(parseInt(this.reportId), 'property')
          this.$router.push({ name: 'Dashboard' })
        })
    },
    safelyNavigate(to: RouteLocation) {
      this.hasUnsavedChanges = false
      this.$router.push(to)
    },
    showUnsavedChangesDialog() {
      return new Promise((resolve) => {
        this.$q
          .dialog({
            title: 'Leaving Report Editing',
            message:
              'You are about to leave this page. Any changes you have made will be lost. Are you sure you want to leave?',
            cancel: {
              label: 'Stay',
              flat: true,
              noCaps: true
            },
            ok: {
              label: 'Leave Page',
              flat: true,
              noCaps: true
            },
            persistent: true
          })
          .onOk(() => {
            resolve(true)
          })
          .onCancel(() => {
            resolve(false)
          })
      })
    }
  }
}
</script>
